import type { IUser } from '@/shared/api/types/user-types'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check, Copy, KeyRound, Loader2, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useChangeUserPasswordMutation, useResetUserPasswordMutation } from '@/entities/user'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'

const changePasswordSchema = z
  .object({
    newPassword: z.string().min(8, 'user.validation.passwordMin'),
    confirmPassword: z.string().min(8, 'user.validation.passwordMin'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'auth.passwordMismatch',
  })

type ChangePasswordValues = z.infer<typeof changePasswordSchema>

interface UserPasswordActionsProps {
  user: IUser
  align?: 'horizontal' | 'vertical'
}

export function UserPasswordActions({ user, align = 'horizontal' }: UserPasswordActionsProps) {
  const { t } = useTranslation()
  const [changeOpen, setChangeOpen] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const changeMutation = useChangeUserPasswordMutation()
  const resetMutation = useResetUserPasswordMutation()

  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  const getErrorText = (message: string | undefined) =>
    message ? t(message, { defaultValue: message }) : undefined

  async function onChangeSubmit(values: ChangePasswordValues) {
    try {
      await changeMutation.mutateAsync({
        id: user.id,
        payload: { newPassword: values.newPassword },
      })
      showToast.success(t('user.passwordChangeSuccess'))
      form.reset()
      setChangeOpen(false)
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  async function handleConfirmReset() {
    try {
      const result = await resetMutation.mutateAsync(user.id)
      setTemporaryPassword(result.temporaryPassword)
      showToast.success(t('user.resetSuccess'))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  function handleCloseReset() {
    setResetOpen(false)
    setTemporaryPassword(null)
    setCopied(false)
  }

  async function handleCopy() {
    if (!temporaryPassword) return
    try {
      await navigator.clipboard.writeText(temporaryPassword)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      showToast.error(t('common.operationFailed'))
    }
  }

  return (
    <>
      <div className={align === 'vertical' ? 'flex flex-col gap-2' : 'flex flex-wrap gap-2'}>
        <Button variant="outline" size="sm" onClick={() => setChangeOpen(true)}>
          <KeyRound className="mr-1.5 h-3.5 w-3.5" />
          {t('user.changePassword')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setResetOpen(true)
            setTemporaryPassword(null)
          }}
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
          {t('user.resetPassword')}
        </Button>
      </div>

      <Dialog open={changeOpen} onOpenChange={(open) => !open && setChangeOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('user.changePassword')}</DialogTitle>
            <DialogDescription>
              {t('user.changePasswordHint', { username: user.username })}
            </DialogDescription>
          </DialogHeader>
          <form
            id="change-password-form"
            onSubmit={form.handleSubmit(onChangeSubmit)}
            className="space-y-4"
            autoComplete="off"
          >
            <FormField
              label={t('user.newPassword')}
              required
              error={getErrorText(form.formState.errors.newPassword?.message)}
            >
              <Input
                type="password"
                autoComplete="new-password"
                {...form.register('newPassword')}
              />
            </FormField>
            <FormField
              label={t('auth.confirmPassword')}
              required
              error={getErrorText(form.formState.errors.confirmPassword?.message)}
            >
              <Input
                type="password"
                autoComplete="new-password"
                {...form.register('confirmPassword')}
              />
            </FormField>
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              form="change-password-form"
              type="submit"
              disabled={changeMutation.isPending}
              aria-busy={changeMutation.isPending}
            >
              {changeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={(open) => !open && handleCloseReset()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('user.resetPassword')}</DialogTitle>
            <DialogDescription>
              {temporaryPassword
                ? t('user.resetPasswordResultHint')
                : t('user.resetPasswordConfirm', { username: user.username })}
            </DialogDescription>
          </DialogHeader>

          {temporaryPassword && (
            <div className="flex items-center gap-2">
              <Input value={temporaryPassword} readOnly className="font-mono" />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopy}
                aria-label={t('common.copy', { defaultValue: 'Copy' })}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}

          <DialogFooter>
            {temporaryPassword ? (
              <Button onClick={handleCloseReset}>{t('common.close')}</Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleCloseReset}>
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleConfirmReset}
                  disabled={resetMutation.isPending}
                  aria-busy={resetMutation.isPending}
                >
                  {resetMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('common.confirm')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
